import { differenceInMinutes, isValid } from 'date-fns';
import { TimeIntervalTypeValues } from 'src/components/oneCorp/features/MealCredit/common/formFields/mealCreditFormFields.utils';
import { BudgetResetTypeValues } from 'src/components/oneCorp/features/MealCredit/common/formFields/Select/BudgetResetTypeSelect/BudgetResetTypeSelect.utils';
import {
  IMealCreditFormFields,
  MealCreditLength,
  setTimeToDate,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import {
  ALL_DAY_MEAL_CREDIT_DURATION,
  VALID_EVERY_DAY,
} from 'src/models/LineOfCreditModelUtils';
import { getDailyPeriodEndFromStartDay } from 'src/components/oneCorp/features/MealCredit/utils/getDailyPeriodEndFromStartDay';

export type DurationMinutesProps = Pick<
  IMealCreditFormFields,
  | 'timeIntervalType'
  | 'startTime'
  | 'startTimeMeridian'
  | 'endTime'
  | 'endTimeMeridian'
>;

export type ValidDaysOfWeekProps = {
  days: IMealCreditFormFields['days'];
  isMultiDaysLengthBudget: boolean;
  isRepeating: boolean;
};

export type BudgetResetPeriodProps = {
  budgetLength: IMealCreditFormFields['budgetLength'];
  numberOfConsecutiveDays: IMealCreditFormFields['numberOfConsecutiveDays'];
  budgetResetType: IMealCreditFormFields['budgetResetType'];
  budgetResetPeriod: IMealCreditFormFields['budgetResetPeriod'];
};

/**
 * Fixed reference date (Jan 15, 2025) used to avoid DST-related bugs in duration calculations.
 *
 * Without this fix, calculating duration on DST transition dates would be incorrect:
 * - Spring forward (March): 1 AM - 4 AM would return 120 minutes instead of 180 (loses 1 hour)
 * - Fall back (November): 1 AM - 4 AM would return 240 minutes instead of 180 (gains 1 hour)
 *
 * Since we only need the time difference, using a fixed non-DST date ensures accuracy.
 */

const DST_SAFE_REFERENCE_DATE = new Date(2025, 0, 15);

export const getDurationMinutes = ({
  timeIntervalType,
  startTime,
  startTimeMeridian,
  endTime,
  endTimeMeridian,
}: DurationMinutesProps): number => {
  if (timeIntervalType === TimeIntervalTypeValues.ALL_DAY) {
    return ALL_DAY_MEAL_CREDIT_DURATION;
  }
  const startDate = DST_SAFE_REFERENCE_DATE;
  const endDate = getDailyPeriodEndFromStartDay(startDate, timeIntervalType);

  const parsedStartTime = setTimeToDate(
    startDate,
    startTime,
    startTimeMeridian
  );
  const parsedEndTime = setTimeToDate(endDate, endTime, endTimeMeridian);

  if (!isValid(parsedStartTime) || !isValid(parsedEndTime)) {
    return 0;
  }
  return differenceInMinutes(parsedEndTime, parsedStartTime);
};

export const getValidDaysOfWeekForPayload = ({
  days,
  isMultiDaysLengthBudget,
  isRepeating,
}: ValidDaysOfWeekProps): number[] => {
  if (isMultiDaysLengthBudget || isRepeating) {
    return days.reduce(
      (validDays: number[], day: boolean, currentIndex: number) => {
        return day ? [...validDays, currentIndex + 1] : validDays;
      },
      []
    );
  }
  return VALID_EVERY_DAY;
};

export const getBudgetResetPeriodForPayload = ({
  budgetLength,
  numberOfConsecutiveDays,
  budgetResetType,
  budgetResetPeriod,
}: BudgetResetPeriodProps): number => {
  const isCustomMonthlyLengthBudget =
    budgetLength === MealCreditLength.CUSTOM &&
    budgetResetType === BudgetResetTypeValues.MONTHS;

  switch (budgetLength) {
    case MealCreditLength.DAILY:
      return 24;
    case MealCreditLength.MONTHLY:
      return 1;
    case MealCreditLength.WEEKLY:
    case MealCreditLength.CUSTOM:
    default:
      return isCustomMonthlyLengthBudget
        ? Number(budgetResetPeriod)
        : numberOfConsecutiveDays;
  }
};
